import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { EngineService } from 'src/app/engine/engine.service';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { SetCamera } from 'src/app/engine/state/camera/engine.camera.actions';
import { QuizLifecycleState } from 'src/app/quiz/models/quiz-lifecycle.enum';
import { QuizQuestionType } from 'src/app/quiz/models/quiz-question-type.enum';
import { QuizQuestionUnion } from 'src/app/quiz/models/quiz-question.model';
import { QuizActions } from 'src/app/quiz/state/quiz.actions';
import { QuizLaunchMode, QuizState } from 'src/app/quiz/state/quiz.state';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { ViewService } from '../../view.service';

@Injectable({
  providedIn: 'root',
})
export class QuizService {
  constructor(
    private store: Store,
    private engineService: EngineService,
    private viewService: ViewService,
  ) {}

  startQuiz() {
    this.viewService.toggleColumn(true);

    // enable quiz
    this.store.dispatch(new QuizActions.SetQuizActive(true));

    // go to first question
    const questionId = this.store.selectSnapshot(QuizState.getAllQuestions)[0].id;
    this.store.dispatch(new QuizActions.SetActiveQuestion(questionId));
  }

  resetQuiz() {
    this.viewService.clearFadedNodes();
    this.viewService.clearSelectedNodes();

    this.store.dispatch(new QuizActions.SetQuizActive(false));
    this.store.dispatch(new QuizActions.SetActiveSubmission(undefined));
    this.store.dispatch(new QuizActions.ClearSubmissions());
    this.store.dispatch(new QuizActions.SetQuizLifecycle(QuizLifecycleState.Question));
  }

  exitQuiz() {
    // cleanup state
    this.resetQuiz();

    // reset chapter camera
    setTimeout(() => {
      this.store.dispatch(
        new SetCamera(this.store.selectSnapshot(StoryChaptersState.getChapterCamera), false),
      );
    });
  }

  submitQuestion(questionId: string, answer: string[]) {
    const question = this.store
      .selectSnapshot(QuizState.getAllQuestions)
      .find((q) => q.id === questionId);
    const { launchMode, quizSettings } = this.store.selectSnapshot(QuizState);

    this.store.dispatch(new QuizActions.SubmitQuestionAnswer({ questionId: question.id, answer }));
    this.store.dispatch(
      new QuizActions.SetActiveSubmission({
        questionId: question.id,
        answer: answer,
      }),
    );

    this.engineService.fireEvent(EventsEnum.QuizAnswerSubmitted, {
      questionId: question.id,
      answer: answer,
    });

    // set explination enabled
    const explanationEnabled =
      launchMode === QuizLaunchMode.Learning &&
      question.type !== QuizQuestionType.Instructions &&
      quizSettings.showExplanation !== false;

    if (explanationEnabled) {
      this.store.dispatch(new QuizActions.SetQuizLifecycle(QuizLifecycleState.Explanation));
    } else {
      this.nextQuestion();
    }
  }

  nextQuestion() {
    const hasQuestionRemaining = this.store.selectSnapshot(QuizState.hasQuestionRemaining);
    if (hasQuestionRemaining) {
      this.store.dispatch(new QuizActions.SetQuizLifecycle(QuizLifecycleState.Question));
      this.viewService.clearFadedNodes();
      this.viewService.clearSelectedNodes();
      this.store.dispatch(new QuizActions.NextQuestion());
    } else {
      this.store.dispatch(new QuizActions.SetQuizLifecycle(QuizLifecycleState.Review));
      this.engineService.fireEvent(EventsEnum.QuizCompleted, true);
    }
  }
}
